import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { coverage, headingOf, mark, sectorPath, SECTORS } from '../../utils/events/scan';

/*
 * The radius-scan helper (docs/GAME_DESIGN.md Part III section 19, PR14):
 * standing at a waypoint, the player turns round and the ring fills with each
 * compass sector of the search circle they have faced, so no side of it goes
 * unsearched. Client only (DeviceOrientation); iOS asks permission first, on
 * a tap. True AR (WebXR) is out of reach on iOS Safari, so this is not that.
 */
export default function RadiusScan({ radius }) {
  const { t } = useTranslation('events');
  const [state, setState] = useState('idle');   // idle | scanning | denied | unsupported
  const [seen, setSeen] = useState(() => Array(SECTORS).fill(false));

  useEffect(() => {
    if (state !== 'scanning') return undefined;
    const on = (event) => {
      const heading = headingOf(event);
      if (heading !== null) setSeen((s) => mark(s, heading));
    };
    window.addEventListener('deviceorientationabsolute', on);
    window.addEventListener('deviceorientation', on);
    return () => {
      window.removeEventListener('deviceorientationabsolute', on);
      window.removeEventListener('deviceorientation', on);
    };
  }, [state]);

  const start = async () => {
    const Orientation = typeof window !== 'undefined' ? window.DeviceOrientationEvent : undefined;
    if (!Orientation) { setState('unsupported'); return; }
    if (typeof Orientation.requestPermission === 'function') {
      try {
        if (await Orientation.requestPermission() !== 'granted') { setState('denied'); return; }
      } catch {
        setState('denied');
        return;
      }
    }
    setSeen(Array(SECTORS).fill(false));
    setState('scanning');
  };

  const share = Math.round(coverage(seen) * 100);
  return (
    <section data-testid="radius-scan" data-state={state} data-coverage={share} aria-labelledby="radius-scan-title"
      style={{ border: '1px solid var(--c-border)', borderRadius: 12, padding: 10, marginTop: 8 }}>
      <h3 id="radius-scan-title" style={{ margin: '0 0 4px', fontSize: 15 }}>{t('scan.title')}</h3>
      <p style={{ margin: '0 0 8px', fontSize: 13 }}>{t('scan.help', { radius: radius || 3 })}</p>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <svg width="96" height="96" viewBox="0 0 100 100" role="img" aria-label={t('scan.covered', { share })}>
          {seen.map((on, i) => (
            <path key={i} d={sectorPath(i, SECTORS, 50, 22, 46)} data-testid={`radius-scan-sector-${i}`}
              data-seen={on ? 'true' : 'false'}
              fill={on ? 'var(--c-primary, #2e7d32)' : 'var(--c-border, #ccc)'} stroke="var(--c-surface, #fff)" />
          ))}
          <text x="50" y="55" textAnchor="middle" fontSize="16" fill="currentColor">{share}%</text>
        </svg>
        <div>
          {state !== 'scanning' && (
            <button type="button" className="pn-btn" style={{ minHeight: 44 }} onClick={start}
              data-testid="radius-scan-start">{t('scan.start')}</button>
          )}
          {state === 'scanning' && share === 100 && <p role="status">{t('scan.done')}</p>}
          {state === 'denied' && <p role="alert">{t('scan.denied')}</p>}
          {state === 'unsupported' && <p role="alert">{t('scan.unsupported')}</p>}
        </div>
      </div>
    </section>
  );
}
